import React, { useRef, useState, ReactElement } from 'react';
import { Tooltip, TooltipProps, tooltipClasses } from '@mui/material';
import { styled } from '@mui/material/styles';

const CustomWidthTooltip = styled(({ className, ...props }: TooltipProps) => <Tooltip {...props} classes={{ popper: className }} />)({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 500,
  },
});

interface CustomTooltipProps extends Omit<TooltipProps, 'children'> {
  children: ReactElement;
}

export const CustomTooltip: React.FC<CustomTooltipProps> = ({ children, title, ...props }) => {
  const textRef = useRef<HTMLElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  const handleMouseEnter = () => {
    const el = textRef.current;
    if (!el) return;
    setIsOverflowing(el.scrollWidth > el.clientWidth);
  };

  // Clone the child and attach the ref and mouse enter handler
  const childWithRef = React.cloneElement(children, {
    ref: textRef,
    onMouseEnter: handleMouseEnter,
  });

  // The Tooltip is always mounted and suppressed by passing an empty title, rather than being
  // mounted only once overflow is detected. Mounting it on demand meant the wrapper appeared after
  // the hover that detected the overflow had already been handled, so the first hover never showed
  // a tooltip — the user had to move away and back.
  return (
    <CustomWidthTooltip title={isOverflowing ? title : ''} {...props} arrow={false}>
      {childWithRef}
    </CustomWidthTooltip>
  );
};
